/**
 * ConvexNotificationContext - Convex-based notification provider
 *
 * This provider replaces the REST API-based NotificationContext with Convex queries.
 * It provides the same interface for backwards compatibility while using real-time
 * Convex subscriptions instead of SSE/polling.
 *
 * Features:
 * - Real-time notification updates via Convex subscriptions
 * - Same interface as NotificationContext for drop-in replacement
 * - Bell and banner notification separation
 * - Page-based notification filtering
 */

import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useQuery, useMutation, useConvexAuth } from "convex/react";
import { api } from "@careercard/convex/convex/_generated/api";
import type { Doc, Id } from "@careercard/convex/convex/_generated/dataModel";
import { useToast } from "./ToastContext";
import { createLogger } from "../utils/logger-ssr-safe";

const logger = createLogger({ module: "ConvexNotificationContext" });

// =============================================================================
// Types
// =============================================================================

/**
 * Notification type string - compatible with Convex storage (string)
 * but also maps to the union type used by UI components
 */
export type NotificationType = "info" | "success" | "warning" | "error";

/**
 * Notification type compatible with existing UI components
 * Matches the UserNotification interface from the legacy system
 */
export interface UserNotification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  notification_type: NotificationType;
  delivery_method: string;
  action_url?: string;
  action_text?: string;
  metadata?: Record<string, unknown>;
  priority: number;
  category?: string;
  source_type?: string;
  source_id?: string;
  is_critical?: boolean;
  status?: string;
  read_at: string | null;
  delivered_at: string | null;
  dismissed_at: string | null;
  created_at: string;
  updated_at: string;
  template?: {
    key?: string;
  };
}

/**
 * Context type - matches the existing NotificationContext interface
 */
interface ConvexNotificationContextType {
  // Bell notifications
  bellNotifications: UserNotification[];
  unreadCount: number;

  // Banner notifications
  bannerNotifications: UserNotification[];
  activeBanner: UserNotification | null;

  // History notifications (dismissed)
  historyNotifications: UserNotification[];
  historyLoading: boolean;

  // Page-specific notifications
  getUnreadCountForPage: (page: string) => number;
  getTotalUnreadCount: () => number;

  // Page-based auto-reading
  autoMarkBannersAsReadForPage: (page: string) => Promise<void>;
  getBannerNotificationsForPage: (page: string) => UserNotification[];

  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  dismissNotification: (notificationId: string) => Promise<boolean>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;

  // Loading states
  isLoading: boolean;
  error: string | null;
}

const ConvexNotificationContext = createContext<
  ConvexNotificationContextType | undefined
>(undefined);

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Banner notification types that should be displayed as banners
 */
const BANNER_NOTIFICATION_TYPES = ["system_announcement", "focus_update"];

/**
 * Map notification type from database string to valid NotificationType
 * Defaults to "info" for unknown types
 */
function mapNotificationType(type: string): NotificationType {
  if (type === "error" || type === "warning" || type === "success" || type === "info") {
    return type;
  }
  // Default to "info" for unknown types (e.g., "system_announcement")
  return "info";
}

/**
 * Transform Convex notification document to UserNotification format
 */
function transformToUserNotification(
  doc: Doc<"notifications">
): UserNotification {
  return {
    id: doc._id,
    user_id: doc.userId,
    title: doc.title,
    message: doc.message,
    notification_type: mapNotificationType(doc.notificationType),
    delivery_method: doc.deliveryMethod ?? "in_app",
    action_url: doc.actionUrl,
    action_text: doc.actionText,
    metadata: doc.metadata as Record<string, unknown> | undefined,
    priority: doc.priority ?? 3,
    category: doc.category,
    source_type: doc.sourceType,
    source_id: doc.sourceId,
    is_critical: doc.isCritical,
    status: doc.status,
    read_at: doc.readAt ? new Date(doc.readAt).toISOString() : null,
    delivered_at: doc.deliveredAt
      ? new Date(doc.deliveredAt).toISOString()
      : null,
    dismissed_at: doc.dismissedAt
      ? new Date(doc.dismissedAt).toISOString()
      : null,
    created_at: new Date(doc.createdAt).toISOString(),
    updated_at: new Date(doc.updatedAt).toISOString(),
  };
}

/**
 * Check if a notification should be shown as a banner
 */
function isBannerNotification(notification: UserNotification): boolean {
  const isBannerType = BANNER_NOTIFICATION_TYPES.includes(
    notification.notification_type
  );
  const isCritical = notification.is_critical === true;
  return isBannerType || isCritical;
}

// =============================================================================
// Hook Export
// =============================================================================

/**
 * Hook to access the Convex notification context
 * Provides the same interface as useNotifications for compatibility
 */
export const useConvexNotifications = () => {
  const context = useContext(ConvexNotificationContext);
  if (!context) {
    throw new Error(
      "useConvexNotifications must be used within a ConvexNotificationProvider"
    );
  }
  return context;
};

/**
 * Alias for backwards compatibility with existing components
 * that use useNotifications from NotificationContext
 */
export const useNotifications = useConvexNotifications;

// =============================================================================
// Provider Component
// =============================================================================

export const ConvexNotificationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { showToast } = useToast();
  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth();

  // Subscribe to notifications via Convex - only when authenticated
  // Using "skip" prevents the query from running when not authenticated
  const notificationsData = useQuery(
    api.notifications.list,
    isAuthenticated ? { limit: 50 } : "skip"
  );

  // Subscribe to dismissed notifications history - only when authenticated
  const historyData = useQuery(
    api.notifications.list,
    isAuthenticated ? { limit: 50, dismissedOnly: true } : "skip"
  );

  // Subscribe to unread count - only when authenticated
  const unreadCountData = useQuery(
    api.notifications.unreadCount,
    isAuthenticated ? {} : "skip"
  );

  // Mutations
  const markAsReadMutation = useMutation(api.notifications.markAsRead);
  const markAllReadMutation = useMutation(api.notifications.markAllRead);
  const dismissMutation = useMutation(api.notifications.dismiss);

  // Transform and categorize notifications
  const { bellNotifications, bannerNotifications, activeBanner } =
    useMemo(() => {
      if (!notificationsData?.notifications) {
        return {
          bellNotifications: [] as UserNotification[],
          bannerNotifications: [] as UserNotification[],
          activeBanner: null as UserNotification | null,
        };
      }

      const allNotifications = notificationsData.notifications.map(
        transformToUserNotification
      );

      // Separate bell and banner notifications
      const bell: UserNotification[] = [];
      const banners: UserNotification[] = [];

      for (const notification of allNotifications) {
        // Skip dismissed notifications for display purposes
        if (notification.dismissed_at) continue;

        if (isBannerNotification(notification)) {
          banners.push(notification);
        } else {
          bell.push(notification);
        }
      }

      // Find the active banner (highest priority undismissed and unread)
      const activeBannerCandidate =
        banners
          .filter((n) => !n.read_at && !n.dismissed_at)
          .sort((a, b) => b.priority - a.priority)[0] || null;

      return {
        bellNotifications: bell,
        bannerNotifications: banners,
        activeBanner: activeBannerCandidate,
      };
    }, [notificationsData?.notifications]);

  // Transform history notifications
  const historyNotifications = useMemo(() => {
    if (!historyData?.notifications) {
      return [] as UserNotification[];
    }
    return historyData.notifications.map(transformToUserNotification);
  }, [historyData?.notifications]);

  // Unread count from Convex query
  const unreadCount = unreadCountData ?? 0;

  // Loading state - includes auth loading and query loading
  // When not authenticated, we're not loading (queries are skipped)
  const isLoading = isAuthLoading || (isAuthenticated && (
    notificationsData === undefined || unreadCountData === undefined
  ));

  // History loading state
  const historyLoading = isAuthenticated && historyData === undefined;

  // Mark a single notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await markAsReadMutation({
          notificationId: notificationId as Id<"notifications">,
        });
        logger.debug("Marked notification as read", { notificationId });
      } catch (error) {
        logger.error("Failed to mark notification as read:", error);
        showToast("Failed to mark notification as read", "error");
        throw error;
      }
    },
    [markAsReadMutation, showToast]
  );

  // Dismiss a notification
  const dismissNotification = useCallback(
    async (notificationId: string): Promise<boolean> => {
      try {
        await dismissMutation({
          notificationId: notificationId as Id<"notifications">,
        });
        logger.debug("Dismissed notification", { notificationId });
        return true;
      } catch (error) {
        logger.error("Failed to dismiss notification:", error);
        showToast("Failed to dismiss notification", "error");
        return false;
      }
    },
    [dismissMutation, showToast]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await markAllReadMutation({});
      showToast("All notifications marked as read", "success");
      logger.debug("Marked all notifications as read");
    } catch (error) {
      logger.error("Failed to mark all notifications as read:", error);
      showToast("Failed to mark notifications as read", "error");
      throw error;
    }
  }, [markAllReadMutation, showToast]);

  // Refresh is a no-op for Convex (data is real-time)
  const refreshNotifications = useCallback(async () => {
    // Convex queries are reactive, no manual refresh needed
    logger.debug("refreshNotifications called - no-op for Convex");
  }, []);

  // Get unread count for a specific page based on banner notifications
  const getUnreadCountForPage = useCallback(
    (page: string): number => {
      return bannerNotifications.filter((notification) => {
        const targetPage = notification.metadata?.target_page;
        return (
          targetPage === page && !notification.read_at && !notification.dismissed_at
        );
      }).length;
    },
    [bannerNotifications]
  );

  // Get total unread count (bell + banner)
  const getTotalUnreadCount = useCallback((): number => {
    const bannerCount = bannerNotifications.filter(
      (n) => !n.read_at && !n.dismissed_at
    ).length;
    return unreadCount + bannerCount;
  }, [unreadCount, bannerNotifications]);

  // Get banner notifications for a specific page
  const getBannerNotificationsForPage = useCallback(
    (page: string): UserNotification[] => {
      return bannerNotifications.filter((notification) => {
        const targetPage = notification.metadata?.target_page;
        return targetPage === page;
      });
    },
    [bannerNotifications]
  );

  // Auto-mark banner notifications as read when landing on a page
  const autoMarkBannersAsReadForPage = useCallback(
    async (page: string): Promise<void> => {
      const pageNotifications = getBannerNotificationsForPage(page);
      const unreadNotifications = pageNotifications.filter(
        (n) => !n.read_at && !n.dismissed_at
      );

      // If there's exactly 1 unread banner notification for this page, auto-mark it as read
      if (unreadNotifications.length === 1) {
        const notification = unreadNotifications[0];
        logger.debug("Auto-marking single banner as read", {
          page,
          notificationId: notification.id,
        });

        try {
          await markAsRead(notification.id);
        } catch (error) {
          logger.error("Failed to auto-mark banner as read", {
            page,
            notificationId: notification.id,
            error,
          });
        }
      }
    },
    [getBannerNotificationsForPage, markAsRead]
  );

  // Memoized context value
  const value: ConvexNotificationContextType = useMemo(
    () => ({
      bellNotifications,
      unreadCount,
      bannerNotifications,
      activeBanner,
      historyNotifications,
      historyLoading,
      getUnreadCountForPage,
      getTotalUnreadCount,
      autoMarkBannersAsReadForPage,
      getBannerNotificationsForPage,
      markAsRead,
      dismissNotification,
      markAllAsRead,
      refreshNotifications,
      isLoading,
      error: null,
    }),
    [
      bellNotifications,
      unreadCount,
      bannerNotifications,
      activeBanner,
      historyNotifications,
      historyLoading,
      getUnreadCountForPage,
      getTotalUnreadCount,
      autoMarkBannersAsReadForPage,
      getBannerNotificationsForPage,
      markAsRead,
      dismissNotification,
      markAllAsRead,
      refreshNotifications,
      isLoading,
    ]
  );

  return (
    <ConvexNotificationContext.Provider value={value}>
      {children}
    </ConvexNotificationContext.Provider>
  );
};

export default ConvexNotificationProvider;
